import { forwardRef, MouseEvent, useEffect, useState } from "react";

import { Icon } from "@nel-ui/icon-react";
import { getDataRef } from "@nel-ui/js-helpers";
import { createNELStyles } from "@nel-ui/react";
import cx from "classnames";

import styles from "./toggle-switch.css";
import { IToggleSwitchProps } from "../../interfaces";

export const useToggleSwitchStyles = createNELStyles(styles, {
  name: "ToggleSwitch",
});

const ToggleSwitch = forwardRef<HTMLButtonElement, IToggleSwitchProps>(
  function ToggleSwitchWithRef(
    {
      "aria-label": ariaLabel,
      className,
      "data-ref": dataRef = "",
      defaultToggle = false,
      label,
      onChange,
      ...props
    }: IToggleSwitchProps,
    ref
  ) {
    const classes = useToggleSwitchStyles();
    const [isToggled, setIsToggled] = useState<boolean>(defaultToggle);

    useEffect(() => {
      setIsToggled(defaultToggle);
    }, [defaultToggle]);

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();

      if (onChange) onChange(event);
    };

    return (
      <div
        {...props}
        className={cx(classes.toggleSwitchContainer)}
        data-ref={getDataRef(dataRef, "toggleSwitch")}
      >
        {label && (
          <label
            className={cx(classes.toggleSwitchLabel)}
            data-ref={getDataRef(dataRef, "toggleSwitchLabel")}
          >
            {label}
          </label>
        )}
        <button
          data-testid="toggle-switch"
          aria-checked={isToggled}
          aria-label={ariaLabel}
          className={cx(classes.toggleSwitchButtonContainer, {
            [classes.toggleSwitchButtonOn]: isToggled,
            [classes.toggleSwitchButtonOff]: !isToggled,
          })}
          data-ref={getDataRef(dataRef, "toggleSwitchButton")}
          ref={ref}
          role="switch"
          type="button"
          onClick={handleClick}
        >
          <span
            className={cx(className, classes.toggleSwitchButtonCircle, {
              [classes.toggleSwitchButtonCircleOn]: isToggled,
              [classes.toggleSwitchButtonCircleOff]: !isToggled,
            })}
          >
            {isToggled && <Icon name="static-tick" size={24} />}
          </span>
        </button>
      </div>
    );
  }
);

export default ToggleSwitch;
